<template>
  <div class="agent-feedback-overview" v-loading="loading">
    <el-alert
      v-if="serviceHint"
      class="service-hint"
      :title="serviceHint"
      type="info"
      :closable="false"
      show-icon
    />

    <el-row :gutter="16" class="stats-row">
      <el-col :xs="12" :sm="6">
        <div class="stat-card">
          <div class="stat-value">{{ stats.total }}</div>
          <div class="stat-label">反馈总数</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="stat-card is-helpful">
          <div class="stat-value">{{ stats.helpful }}</div>
          <div class="stat-label">有帮助</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="stat-card is-unhelpful">
          <div class="stat-value">{{ stats.unhelpful }}</div>
          <div class="stat-label">没帮助</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="stat-card">
          <div class="stat-value">{{ stats.helpful_rate }}%</div>
          <div class="stat-label">好评率</div>
        </div>
      </el-col>
    </el-row>

    <div class="toolbar">
      <el-input
        v-model="filters.project_id"
        placeholder="按项目 ID 筛选"
        clearable
        class="toolbar-item"
        @keyup.enter="handleSearch"
      />
      <el-select v-model="filters.rating" placeholder="反馈类型" clearable class="toolbar-item">
        <el-option label="有帮助" value="helpful" />
        <el-option label="没帮助" value="unhelpful" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="handleReset">重置</el-button>
      <el-button :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-table :data="items" border stripe empty-text="暂无反馈数据">
      <el-table-column prop="updated_at" label="提交时间" width="170">
        <template #default="{ row }">{{ formatTime(row.updated_at || row.created_at) }}</template>
      </el-table-column>
      <el-table-column prop="rating" label="反馈" width="100">
        <template #default="{ row }">
          <el-tag :type="row.rating === 'helpful' ? 'success' : 'danger'" size="small">
            {{ row.rating === 'helpful' ? '有帮助' : '没帮助' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="理由标签" width="160" show-overflow-tooltip>
        <template #default="{ row }">{{ formatTags(row.reason_tags) }}</template>
      </el-table-column>
      <el-table-column prop="active_work" label="当时工作" width="140" show-overflow-tooltip />
      <el-table-column label="使用功能" width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ formatTags(row.features_used) }}</template>
      </el-table-column>
      <el-table-column prop="skill_id" label="Skill" width="120" show-overflow-tooltip />
      <el-table-column prop="model_id" label="模型" width="140" show-overflow-tooltip />
      <el-table-column label="质检" width="90">
        <template #default="{ row }">
          <el-tag v-if="row.qc_status" :type="qcTagType(row.qc_status)" size="small">
            {{ qcLabel(row.qc_status) }}
          </el-tag>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column prop="user_message" label="用户提问" min-width="180" show-overflow-tooltip />
      <el-table-column prop="agent_message" label="Agent 回复" min-width="200" show-overflow-tooltip />
      <el-table-column label="短上下文" min-width="180" show-overflow-tooltip>
        <template #default="{ row }">{{ formatContextPreview(row.short_context) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="90" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link @click="openDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :current-page="page"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog v-model="detailVisible" title="反馈详情" width="780px" destroy-on-close>
      <el-descriptions v-if="activeItem" :column="1" border>
        <el-descriptions-item label="反馈类型">
          {{ activeItem.rating === 'helpful' ? '有帮助' : '没帮助' }}
          <span v-if="activeItem.reason_submitted" class="detail-muted">（已提交理由）</span>
        </el-descriptions-item>
        <el-descriptions-item label="理由标签">{{ formatTags(activeItem.reason_tags) }}</el-descriptions-item>
        <el-descriptions-item label="补充备注">{{ activeItem.comment || '—' }}</el-descriptions-item>
        <el-descriptions-item label="当时工作">{{ activeItem.active_work || '—' }}</el-descriptions-item>
        <el-descriptions-item label="使用功能">{{ formatTags(activeItem.features_used) }}</el-descriptions-item>
        <el-descriptions-item label="Skill">{{ activeItem.skill_id || '—' }}</el-descriptions-item>
        <el-descriptions-item label="模型">{{ activeItem.model_id || '—' }}</el-descriptions-item>
        <el-descriptions-item label="Trace / Run">
          {{ activeItem.trace_id || '—' }} / {{ activeItem.run_id || '—' }}
        </el-descriptions-item>
        <el-descriptions-item label="质检状态">
          <el-tag v-if="activeItem.qc_status" :type="qcTagType(activeItem.qc_status)" size="small">
            {{ qcLabel(activeItem.qc_status) }}
          </el-tag>
          <span v-else>—</span>
        </el-descriptions-item>
        <el-descriptions-item label="质检摘要">
          <div class="detail-block">{{ formatQcSummary(activeItem.qc_summary) }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="媒体引用">
          <div v-if="mediaRefItems(activeItem.image_refs).length" class="media-ref-grid">
            <a
              v-for="(item, index) in mediaRefItems(activeItem.image_refs)"
              :key="`${item.src}-${index}`"
              class="media-ref-card"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                v-if="item.previewSrc"
                :src="item.previewSrc"
                :alt="item.title || item.roleLabel"
                class="media-ref-thumb"
              />
              <div v-else class="media-ref-placeholder">{{ item.kind === 'model' ? '3D' : 'IMG' }}</div>
              <div class="media-ref-meta">
                <span>{{ item.roleLabel }}</span>
                <small>{{ item.title || item.fileLabel }}</small>
              </div>
            </a>
          </div>
          <div v-else class="detail-block">—</div>
        </el-descriptions-item>
        <el-descriptions-item label="来源标签">{{ activeItem.agent_meta || '—' }}</el-descriptions-item>
        <el-descriptions-item label="用户提问">
          <div class="detail-block">{{ activeItem.user_message || '—' }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="Agent 回复">
          <div class="detail-block">{{ activeItem.agent_message || '—' }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="短暂上下文">
          <div class="detail-block">{{ formatContextFull(activeItem.short_context) }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="提交时间">{{ formatTime(activeItem.updated_at || activeItem.created_at) }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { getAgentFeedbackStats, listAgentFeedback } from '@/api/agentFeedback'

const loading = ref(false)
const items = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const detailVisible = ref(false)
const activeItem = ref(null)
const serviceHint = ref('')

const stats = reactive({
  total: 0,
  helpful: 0,
  unhelpful: 0,
  helpful_rate: 0,
})

const filters = reactive({
  project_id: '',
  rating: '',
})

function formatTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('zh-CN', { hour12: false })
}

function formatTags(tags) {
  if (!Array.isArray(tags) || !tags.length) return '—'
  return tags.filter(Boolean).join('、')
}

function formatContextPreview(context) {
  if (!Array.isArray(context) || !context.length) return '—'
  return context
    .slice(-3)
    .map((turn) => `${turn.role === 'user' ? '用户' : 'Agent'}: ${turn.text || ''}`)
    .join(' | ')
}

function formatContextFull(context) {
  if (!Array.isArray(context) || !context.length) return '—'
  return context
    .map((turn) => `${turn.role === 'user' ? '用户' : 'Agent'}: ${turn.text || ''}`)
    .join('\n')
}

function qcLabel(status) {
  if (status === 'failed') return '未通过'
  if (status === 'review') return '建议检查'
  if (status === 'passed') return '通过'
  return status || '—'
}

function qcTagType(status) {
  if (status === 'failed') return 'danger'
  if (status === 'review') return 'warning'
  if (status === 'passed') return 'success'
  return 'info'
}

function formatQcSummary(summary) {
  if (!summary) return '—'
  const data = typeof summary === 'string'
    ? (() => { try { return JSON.parse(summary) } catch { return null } })()
    : summary
  if (!data || typeof data !== 'object') return String(summary)
  const failed = Array.isArray(data.failed_ids) ? data.failed_ids.filter(Boolean) : []
  const parts = []
  if (data.status) parts.push(`状态: ${qcLabel(data.status)}`)
  if (failed.length) parts.push(`失败项: ${failed.join('、')}`)
  if (data.requires_user_review) parts.push('需人工建议检查')
  return parts.length ? parts.join('\n') : '—'
}

function looksLikeModelUrl(url = '') {
  return /\.(glb|gltf|fbx|obj|stl)(\?|#|$)/i.test(String(url || ''))
}

function isImagePreviewUrl(url = '') {
  const src = String(url || '').trim()
  if (!src || src.startsWith('blob:')) return false
  if (src.startsWith('data:image/')) return true
  if (looksLikeModelUrl(src)) return false
  return /^(https?:\/\/|\/)/i.test(src)
}

function mediaRefItems(refs) {
  if (!Array.isArray(refs) || !refs.length) return []
  return refs
    .map((item) => {
      const src = String(item?.src || '').trim()
      const previewCandidate = String(item?.previewSrc || item?.preview_src || '').trim()
      const kind = item?.kind === 'model' || item?.role === 'model' || looksLikeModelUrl(src)
        ? 'model'
        : 'image'
      const previewSrc = isImagePreviewUrl(previewCandidate)
        ? previewCandidate
        : (kind === 'image' && isImagePreviewUrl(src) ? src : '')
      const href = src || previewSrc
      if (!href) return null
      const roleLabel = kind === 'model'
        ? '3D模型'
        : (item?.role === 'result' ? '结果' : '参考')
      const fileLabel = (() => {
        try {
          const path = href.split('?')[0]
          return decodeURIComponent(path.split('/').pop() || href)
        } catch {
          return href
        }
      })()
      return {
        kind,
        roleLabel,
        title: String(item?.title || '').trim(),
        fileLabel,
        src,
        previewSrc,
        href,
      }
    })
    .filter(Boolean)
}

function buildQueryParams() {
  const params = {
    limit: pageSize.value,
    offset: (page.value - 1) * pageSize.value,
  }
  if (filters.project_id.trim()) params.project_id = filters.project_id.trim()
  if (filters.rating) params.rating = filters.rating
  return params
}

function buildStatsParams() {
  const params = {}
  if (filters.project_id.trim()) params.project_id = filters.project_id.trim()
  return params
}

async function loadData() {
  loading.value = true
  try {
    const [listRes, statsRes] = await Promise.all([
      listAgentFeedback(buildQueryParams()),
      getAgentFeedbackStats(buildStatsParams()),
    ])
    const listPayload = listRes.data || {}
    const statsPayload = statsRes.data || {}
    items.value = listPayload.items || []
    total.value = Number(listPayload.total ?? listPayload.count ?? items.value.length)
    stats.total = Number(statsPayload.total || 0)
    stats.helpful = Number(statsPayload.helpful || 0)
    stats.unhelpful = Number(statsPayload.unhelpful || 0)
    stats.helpful_rate = Number(statsPayload.helpful_rate || 0)
    serviceHint.value = ''
  } catch (error) {
    const message = error?.response?.data?.error?.message
      || error?.response?.data?.detail
      || error?.message
      || '加载反馈数据失败'
    serviceHint.value = '请确认 Java 后台与 Python AI 服务均已启动，且生产配置 bymy.ai-service-base-url（或 BYMY_AI_SERVICE_BASE_URL）指向 AI 服务地址。'
    ElMessage.error(message)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  page.value = 1
  loadData()
}

function handleReset() {
  filters.project_id = ''
  filters.rating = ''
  page.value = 1
  loadData()
}

function handlePageChange(nextPage) {
  page.value = nextPage
  loadData()
}

function handleSizeChange(nextSize) {
  pageSize.value = nextSize
  page.value = 1
  loadData()
}

function openDetail(row) {
  activeItem.value = row
  detailVisible.value = true
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
.agent-feedback-overview {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.service-hint {
  margin-bottom: 0;
}

.stats-row {
  margin-bottom: 4px;
}

.stat-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 16px;
  min-height: 88px;
}

.stat-card.is-helpful {
  border-color: #b7eb8f;
  background: #f6ffed;
}

.stat-card.is-unhelpful {
  border-color: #ffa39e;
  background: #fff1f0;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.stat-label {
  margin-top: 6px;
  font-size: 13px;
  color: #666;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.toolbar-item {
  width: 220px;
}

.pager {
  display: flex;
  justify-content: flex-end;
}

.detail-block {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.detail-muted {
  margin-left: 8px;
  color: #999;
  font-size: 12px;
}

.media-ref-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.media-ref-card {
  width: 120px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background: #fafafa;
}

.media-ref-card:hover {
  border-color: #c0c4cc;
}

.media-ref-thumb,
.media-ref-placeholder {
  display: block;
  width: 100%;
  height: 88px;
  object-fit: cover;
  background: #f0f2f5;
}

.media-ref-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: #909399;
}

.media-ref-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.3;
}

.media-ref-meta small {
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
